import { useTranslation } from "react-i18next";
import { useCanvasMetadata } from "../../lib/schedule-art/canvas-state";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import {
  createWeekDateLayout, weekDateEntries, weekDateFormatExample, weekDateFormatPatch,
  weekDateFormats, weekDateLayout, weekDateLayouts, weekDateSample,
} from "../../lib/metadata-builder/date-layouts";
import { cn } from "../../lib/utils";
import { NativeSelect } from "../ui/native-select";
import { BuilderPeriodInputs } from "./BuilderPeriodInputs";
import { useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { builderSampleWeek } from "../../lib/metadata-builder/sample-schedule";
import { WeekDateElementEditor } from "./WeekDateElementEditor";

export function WeekDatesStep() {
  const { t } = useTranslation();
  const metadata = useCanvasMetadata();
  const sampleDate = useMetadataBuilderStore(state => state.sampleDate);
  const week = builderSampleWeek(sampleDate);
  const selectedPath = useMetadataEditingStore(state => state.selected);
  const select = useMetadataEditingStore(state => state.select);
  const layout = weekDateLayout(metadata);
  const dates = weekDateEntries(metadata);
  const selected = dates.find(date => date.path === selectedPath) ?? dates[0];
  const labels = { range: t("builder.dates.range"), split: t("builder.dates.split"), parts: t("builder.dates.parts") };
  const choices = layout ? weekDateFormats(layout) : [];
  const elementLabels = layout === "parts"
    ? [t("builder.dates.startMonth"), t("builder.dates.startDay"), t("builder.dates.endMonth"), t("builder.dates.endDay")]
    : [t("metadata.binding.weekStart"), t("metadata.binding.weekEnd")];
  const formatChoice = choices.findIndex(patterns => dates.every(({ element }, index) =>
    metadata.formats[element.format ?? ""] === patterns[layout === "parts" ? index % 2 : 0]));

  return (
    <div className="space-y-3">
      <BuilderPeriodInputs />
      <div className="grid grid-cols-1 gap-2 min-[480px]:grid-cols-3">
        {weekDateLayouts.map(mode => <button key={mode} type="button"
          className={cn("min-w-0 rounded border p-2 text-left", mode === layout ? "border-neutral-900 bg-neutral-100" : "border-neutral-200 hover:bg-neutral-50")}
          onClick={() => {
            if (mode === layout) return;
            endMetadataMerge();
            dispatchBuilderMetadata(createWeekDateLayout(metadata, mode));
            select(null);
          }}>
          <span className="block font-medium">{labels[mode]}</span>
          <span className="mt-1 block text-neutral-500">{weekDateFormatExample(mode, weekDateFormats(mode)[0]!, week)}</span>
        </button>)}
      </div>
      {!dates.length && <p className="text-neutral-500">{t("builder.dates.chooseLayout")}</p>}
      {layout && <label className="grid gap-1 text-xs font-medium">
        {t("metadata.field.format")}
        <NativeSelect value={formatChoice} className="h-8 py-1 text-xs" onChange={event => {
          const patterns = choices[Number(event.target.value)];
          if (!patterns) return;
          endMetadataMerge();
          dispatchBuilderMetadata({ type: "formats/set", formats: weekDateFormatPatch(metadata, layout, patterns) });
        }}>
          {formatChoice === -1 && <option value={-1} disabled>{t("builder.dates.customFormat")}</option>}
          {choices.map((patterns, index) => <option key={index} value={index}>{weekDateFormatExample(layout, patterns, week)}</option>)}
        </NativeSelect>
      </label>}
      {layout === "parts" && <p className="text-neutral-500">{t("builder.dates.partsHelp")}</p>}
      {dates.length > 0 && <div className="flex flex-wrap gap-1">
        {dates.map(({ element, index, path }, ordinal) => <button key={path} type="button"
          className={cn("rounded border px-2 py-1.5", index === selected?.index ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 hover:bg-neutral-50")}
          onClick={() => { endMetadataMerge(); select(path); }}>
          {layout === "range" ? t("builder.dates.range") : elementLabels[ordinal] ?? t("builder.dates.element", { number: ordinal + 1 })} · {weekDateSample(element, metadata, week)}
        </button>)}
      </div>}
      {selected && <WeekDateElementEditor key={selected.path} index={selected.index} />}
      <p className="text-neutral-500">{t("builder.dates.scopeHelp")}</p>
    </div>
  );
}
